import React, { useState } from 'react';
import LaunchModal from '../components/LaunchModal';

const BlogPage: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  return (
    <div className="bg-gray-950 text-white min-h-screen py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h1 className="text-4xl font-bold text-white mb-4">BarnBills Blog</h1>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            Insights, tips, and updates from the world of modern horse farm management
          </p>
        </div>

        {/* Coming Soon Overlay Container */}
        <div className="relative">
          {/* Blurred Background Content */}
          <div className="blur-sm opacity-30 pointer-events-none select-none">
            {/* Featured Article */}
            <div className="mb-16">
              <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
                <div className="h-64 bg-gradient-to-r from-blue-600 to-purple-600"></div>
                <div className="p-8">
                  <div className="flex items-center space-x-4 mb-4">
                    <span className="bg-blue-600 text-white px-3 py-1 rounded-full text-sm">Featured</span>
                    <span className="text-gray-400 text-sm">January 15, 2025</span>
                  </div>
                  <h2 className="text-2xl font-bold text-white mb-4">
                    Why Modern Horse Farms Are Switching to Cloud-Based Management Software
                  </h2>
                  <p className="text-gray-400 mb-6">
                    The equestrian industry is embracing digital transformation. Learn why hundreds of horse farms 
                    are making the switch from outdated desktop software to modern, cloud-based solutions like BarnBills.
                  </p>
                  <span className="text-gray-500 font-medium cursor-not-allowed">
                    Read More →
                  </span>
                </div>
              </div>
            </div>

            {/* Blog Grid */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              <article className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden hover:border-gray-700 transition-colors">
                <div className="h-48 bg-gray-800"></div>
                <div className="p-6">
                  <div className="flex items-center space-x-4 mb-3">
                    <span className="text-gray-400 text-sm">January 12, 2025</span>
                    <span className="text-gray-600">•</span>
                    <span className="text-gray-400 text-sm">5 min read</span>
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-3">
                    5 Signs Your Farm Management Software is Holding You Back
                  </h3>
                  <p className="text-gray-400 text-sm mb-4">
                    Is your current software creating more problems than it solves? Here are the warning signs...
                  </p>
                  <span className="text-gray-500 text-sm font-medium cursor-not-allowed">
                    Read More →
                  </span>
                </div>
              </article>

              <article className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden hover:border-gray-700 transition-colors">
                <div className="h-48 bg-gray-800"></div>
                <div className="p-6">
                  <div className="flex items-center space-x-4 mb-3">
                    <span className="text-gray-400 text-sm">January 10, 2025</span>
                    <span className="text-gray-600">•</span>
                    <span className="text-gray-400 text-sm">3 min read</span>
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-3">
                    The True Cost of Manual Horse Farm Record Keeping
                  </h3>
                  <p className="text-gray-400 text-sm mb-4">
                    Beyond the hours spent on paperwork, manual processes are costing your farm more than you think...
                  </p>
                  <span className="text-gray-500 text-sm font-medium cursor-not-allowed">
                    Read More →
                  </span>
                </div>
              </article>

              <article className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden hover:border-gray-700 transition-colors">
                <div className="h-48 bg-gray-800"></div>
                <div className="p-6">
                  <div className="flex items-center space-x-4 mb-3">
                    <span className="text-gray-400 text-sm">January 8, 2025</span>
                    <span className="text-gray-600">•</span>
                    <span className="text-gray-400 text-sm">4 min read</span>
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-3">
                    Mobile-First Farm Management: Why It Matters
                  </h3>
                  <p className="text-gray-400 text-sm mb-4">
                    Modern farm work happens on the go. Discover why mobile-first software is essential for today's operations...
                  </p>
                  <span className="text-gray-500 text-sm font-medium cursor-not-allowed">
                    Read More →
                  </span>
                </div>
              </article>
            </div>
          </div>

          {/* Coming Soon Overlay */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center">
              <div className="bg-gray-900/95 backdrop-blur-md border border-gray-700 rounded-2xl p-12 max-w-lg mx-auto shadow-2xl">
                <div className="mb-6">
                  <h2 className="text-6xl font-bold bg-gradient-to-r from-blue-400 to-purple-500 bg-clip-text text-transparent mb-4">
                    COMING SOON
                  </h2>
                  <div className="w-24 h-1 bg-gradient-to-r from-blue-500 to-purple-600 mx-auto rounded-full"></div>
                </div>
                <p className="text-xl text-gray-300 mb-8 leading-relaxed">
                  We're crafting valuable insights and expert tips for modern horse farm management. 
                  Our blog will feature industry best practices, software guides, and success stories.
                </p>
                <button 
                  onClick={() => setIsModalOpen(true)}
                  className="inline-flex items-center space-x-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white px-8 py-4 rounded-lg font-semibold transition-all duration-300 hover:from-blue-600 hover:to-purple-700 hover:shadow-lg hover:shadow-blue-500/25 transform hover:scale-105"
                >
                  <span>Get Notified When We Launch</span>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 17h5l-5 5-5-5h5v-12"></path>
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>

        <LaunchModal 
          isOpen={isModalOpen} 
          onClose={() => setIsModalOpen(false)} 
        />
      </div>
    </div>
  );
};

export default BlogPage; 